import { supabase } from "./supabaseClient";

const SUPABASE_URL = String(process.env.REACT_APP_SUPABASE_URL || "").replace(/\/$/, "");
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || "";

const SNAPSHOT_CACHE_KEY = "uniair_service_status_snapshot_v1";
const MANUAL_CACHE_KEY = "uniair_service_status_manual_v1";
const LOCAL_FAILURE_KEY = "uniair_service_status_local_failure_v1";
const STATUS_EVENT = "uniair-service-status-change";
const SNAPSHOT_TTL_MS = 30_000;
const MANUAL_CACHE_TTL_MS = 15 * 60_000;
const LOCAL_FAILURE_TTL_MS = 2 * 60_000;
const LOCAL_FAILURE_CONFIRM_WINDOW_MS = 90_000;
const LOCAL_FAILURE_CONFIRM_COUNT = 2;
let consecutiveAuthProbeFailures = 0;
let snapshotInFlight = null;
// Never let the status page wait for a stalled database connection.
const HEALTH_TIMEOUT_MS = 3_500;
const MANUAL_STATUS_TIMEOUT_MS = 2_500;

export const SERVICE_COMPONENTS = [
  { key: "accounts", label: "Login & accounts", description: "Sign in, sign up and account sessions" },
  { key: "bookings", label: "Bookings", description: "Airport, Private Ride and transport booking flows" },
  { key: "payments", label: "Payments", description: "Card, wallet, vouchers and payment verification" },
  { key: "communications", label: "Emails & SMS", description: "Booking confirmations, reminders and email/SMS delivery" },
  { key: "notifications", label: "Notifications", description: "In-app and push notifications" },
  { key: "maps", label: "Maps & location", description: "Address search, maps and navigation services" },
];

const STATUS_RANK = {
  operational: 0,
  maintenance: 1,
  degraded: 2,
  outage: 3,
};

const safeParse = (value, fallback = null) => {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const readStorage = (key) => {
  try {
    return safeParse(window.localStorage.getItem(key), null);
  } catch {
    return null;
  }
};

const writeStorage = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Best effort only.
  }
};

const removeStorage = (key) => {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore storage errors.
  }
};

const emitStatusChange = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(STATUS_EVENT));
};

const readErrorStatus = (error) => {
  const values = [
    error?.status,
    error?.statusCode,
    error?.context?.status,
    error?.response?.status,
  ];

  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }

  return null;
};

export const isInfrastructureError = (error) => {
  if (!error) return false;

  const status = readErrorStatus(error);
  if ([500, 502, 503, 504, 520, 522, 523, 524].includes(status)) return true;

  const name = String(error?.name || "").toLowerCase();
  const message = String(error?.message || error || "").toLowerCase();

  if (
    name.includes("authretryablefetcherror") ||
    name.includes("fetcherror")
  ) {
    return true;
  }

  return [
    "gateway timeout",
    "failed to fetch",
    "network request failed",
    "networkerror",
    "load failed",
    "signal is aborted",
    "connection timeout",
    "connection terminated due to connection timeout",
    "upstream request timeout",
    "service unavailable",
    "bad gateway",
  ].some((needle) => message.includes(needle));
};

export const friendlyInfrastructureMessage = () =>
  "We’re temporarily unable to reach one of the services UniAir relies on. Your account and password have not been changed. Please try again shortly.";

export const reportInfrastructureFailure = (error, context = "platform") => {
  if (typeof window === "undefined") return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  if (!isInfrastructureError(error)) return;

  const now = Date.now();
  const old = readStorage(LOCAL_FAILURE_KEY);
  const first = Date.parse(old?.first_at || "");
  const sameWindow = old?.context === context &&
    Number.isFinite(first) && now - first < LOCAL_FAILURE_CONFIRM_WINDOW_MS;
  const count = sameWindow ? Math.min(Number(old.count || 0) + 1, 10) : 1;
  writeStorage(LOCAL_FAILURE_KEY, {
    context,
    first_at: new Date(sameWindow ? first : now).toISOString(),
    detected_at: new Date(now).toISOString(),
    expires_at: new Date(now + LOCAL_FAILURE_TTL_MS).toISOString(),
    count,
    status: readErrorStatus(error),
    name: String(error?.name || ""),
  });
  if (count >= LOCAL_FAILURE_CONFIRM_COUNT) emitStatusChange();
};

// A genuinely successful sign-in or a successful Auth + status RPC check
// supersedes a previous device-local failure. Never clear a manual incident.
export const clearInfrastructureFailure = (context = "accounts") => {
  if (typeof window === "undefined") return false;
  const previous = readStorage(LOCAL_FAILURE_KEY);
  if (!previous || (context && previous.context !== context)) return false;
  removeStorage(LOCAL_FAILURE_KEY);
  removeStorage(SNAPSHOT_CACHE_KEY);
  emitStatusChange();
  return true;
};

const getActiveLocalFailure = () => {
  if (typeof window === "undefined") return null;

  const value = readStorage(LOCAL_FAILURE_KEY);
  if (!value?.expires_at) return null;

  const expiry = new Date(value.expires_at).getTime();
  if (!Number.isFinite(expiry) || expiry <= Date.now()) {
    removeStorage(LOCAL_FAILURE_KEY);
    return null;
  }

  // A single failed request can be a navigation cancellation or temporary
  // mobile-network interruption. It must not claim a platform-wide outage.
  return Number(value.count || 0) >= LOCAL_FAILURE_CONFIRM_COUNT ? value : null;
};

const withTimeout = async (factory, timeoutMs = HEALTH_TIMEOUT_MS) => {
  const controller = new AbortController();
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = window.setTimeout(() => {
      controller.abort();
      const error = new Error("Service status check timed out.");
      error.name = "AbortError";
      reject(error);
    }, timeoutMs);
  });

  try {
    // The outer deadline also works if a request ignores its abort signal.
    return await Promise.race([
      Promise.resolve().then(() => factory(controller.signal)),
      timeout,
    ]);
  } finally {
    window.clearTimeout(timer);
  }
};

export const probeSupabaseAuthHealth = async () => {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return {
      state: "offline",
      checked_at: new Date().toISOString(),
      message: "This device appears to be offline.",
    };
  }

  if (!SUPABASE_URL || !SUPABASE_KEY) {
    return {
      state: "unknown",
      checked_at: new Date().toISOString(),
      message: "Health check is not configured.",
    };
  }

  try {
    const response = await withTimeout((signal) =>
      fetch(`${SUPABASE_URL}/auth/v1/health`, {
        method: "GET",
        headers: { apikey: SUPABASE_KEY },
        cache: "no-store",
        signal,
      }),
    );

    if (response.ok) {
      consecutiveAuthProbeFailures = 0;
      const recentFailure = readStorage(LOCAL_FAILURE_KEY);
      const detectedAt = recentFailure?.detected_at
        ? new Date(recentFailure.detected_at).getTime()
        : NaN;

      if (Number.isFinite(detectedAt) && Date.now() - detectedAt > 90_000) {
        removeStorage(LOCAL_FAILURE_KEY);
      }

      return {
        state: "operational",
        status: response.status,
        checked_at: new Date().toISOString(),
      };
    }

    if (response.status >= 500) {
      consecutiveAuthProbeFailures += 1;
      return {
        state:
          response.status === 503 || response.status === 504
            ? "outage"
            : "degraded",
        status: response.status,
        checked_at: new Date().toISOString(),
        message: "UniAir account services are temporarily unreachable.",
      };
    }

    // 4xx can be a header/proxy configuration issue, but doesn't prove
    // customer sign-in is unavailable.
    consecutiveAuthProbeFailures += 1;
    return {
      state: consecutiveAuthProbeFailures >= 2 ? "degraded" : "unknown",
      status: response.status,
      checked_at: new Date().toISOString(),
      message: "Health check returned an unexpected response.",
    };
  } catch (error) {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return {
        state: "offline",
        checked_at: new Date().toISOString(),
        message: "This device appears to be offline.",
      };
    }

    consecutiveAuthProbeFailures += 1;
    return {
      state: consecutiveAuthProbeFailures >= 2 ? "degraded" : "unknown",
      checked_at: new Date().toISOString(),
      message: "Account health check is taking longer than expected.",
      error_name: String(error?.name || ""),
    };
  }
};

const fetchManualStatus = async () => {
  const { data, error } = await withTimeout(
    (signal) => supabase.rpc("get_public_service_status_v1").abortSignal(signal),
    MANUAL_STATUS_TIMEOUT_MS,
  );
  if (error) throw error;

  const result = data || { ok: true, incidents: [] };
  writeStorage(MANUAL_CACHE_KEY, {
    fetched_at: new Date().toISOString(),
    data: result,
  });
  return result;
};

const getCachedManualStatus = () => {
  if (typeof window === "undefined") return null;

  const cached = readStorage(MANUAL_CACHE_KEY);
  if (!cached?.fetched_at || !cached?.data) return null;

  const age = Date.now() - new Date(cached.fetched_at).getTime();
  if (!Number.isFinite(age) || age > MANUAL_CACHE_TTL_MS) return null;

  return cached.data;
};

const applyComponentState = (components, key, state, message = null) => {
  if (!components[key]) return;

  const currentRank = STATUS_RANK[components[key].state] ?? 0;
  const nextRank = STATUS_RANK[state] ?? 0;
  if (nextRank < currentRank) return;

  components[key] = {
    ...components[key],
    state,
    message: message || components[key].message || null,
  };
};

const normalizeIncidentState = (incident) => {
  if (incident?.severity === "outage") return "outage";
  if (incident?.severity === "maintenance") return "maintenance";
  return "degraded";
};

const buildSnapshot = ({
  authProbe,
  manualStatus,
  localFailure,
  manualCheckFailed = false,
}) => {
  const authChecked = ["operational", "degraded", "outage"].includes(
    authProbe?.state,
  );
  const checksIncomplete = manualCheckFailed || !authChecked;
  const components = Object.fromEntries(
    SERVICE_COMPONENTS.map((component) => {
      const verified = component.key === "accounts"
        ? authChecked && !manualCheckFailed
        : !manualCheckFailed;
      return [
        component.key,
        {
          ...component,
          state: verified ? "operational" : "unknown",
          message: verified
            ? null
            : "We couldn't verify this service during the latest check.",
        },
      ];
    }),
  );

  const incidents = Array.isArray(manualStatus?.incidents)
    ? manualStatus.incidents
    : [];

  incidents.forEach((incident) => {
    const state = normalizeIncidentState(incident);
    const target = incident?.service_code || "platform";
    const affected = Array.isArray(incident?.affected_features)
      ? incident.affected_features
      : [];

    if (target !== "platform") {
      applyComponentState(components, target, state, incident?.public_message);
    }

    affected.forEach((feature) => {
      applyComponentState(components, feature, state, incident?.public_message);
    });

    if (target === "platform" && affected.length === 0) {
      Object.keys(components).forEach((key) =>
        applyComponentState(
          components,
          key,
          state,
          incident?.public_message,
        ),
      );
    }
  });

  if (["degraded", "outage"].includes(authProbe?.state)) {
    applyComponentState(
      components,
      "accounts",
      authProbe.state,
      "Login and account services are temporarily experiencing delays.",
    );
  }

  if (localFailure) {
    const context = String(localFailure.context || "platform");

    if (["accounts", "login", "auth"].includes(context)) {
      applyComponentState(
        components,
        "accounts",
        "degraded",
        "Login and account services are temporarily experiencing delays.",
      );
    } else if (components[context]) {
      applyComponentState(
        components,
        context,
        "degraded",
        "This UniAir service is temporarily experiencing delays.",
      );
    } else {
      applyComponentState(
        components,
        "accounts",
        "degraded",
        "Some UniAir services are temporarily experiencing delays.",
      );
    }
  }

  const states = Object.values(components).map(
    (component) => component.state,
  );

  let overallState = "operational";
  if (states.includes("outage")) overallState = "outage";
  else if (states.includes("degraded")) overallState = "degraded";
  else if (states.includes("maintenance")) overallState = "maintenance";
  else if (checksIncomplete) overallState = "degraded";

  const activeIncident = incidents.find(
    (incident) => incident?.public_message,
  );

  const overallMessage =
    activeIncident?.public_message ||
    (overallState === "outage"
      ? "Some UniAir services are temporarily unavailable. We’re monitoring the issue."
      : checksIncomplete && !states.includes("degraded")
        ? "Some UniAir service checks could not be completed. Please try again shortly."
        : overallState === "degraded"
          ? "Some UniAir services are experiencing delays. We’re monitoring the issue."
          : overallState === "maintenance"
            ? "Some UniAir services are undergoing planned maintenance."
            : "All UniAir systems are operating normally.");

  return {
    overall_state: overallState,
    overall_message: overallMessage,
    components: Object.values(components),
    incidents,
    auth_probe: authProbe,
    checked_at: new Date().toISOString(),
  };
};

export const getCachedServiceStatusSnapshot = () => {
  if (typeof window === "undefined") return null;
  const cached = readStorage(SNAPSHOT_CACHE_KEY);
  const checked = Date.parse(cached?.checked_at || "");
  // Do not flash yesterday's outage on a fresh page load.
  return Number.isFinite(checked) && Date.now() - checked < SNAPSHOT_TTL_MS
    ? cached : null;
};

export const getServiceStatusSnapshot = async ({ force = false } = {}) => {
  if (typeof window === "undefined") {
    return buildSnapshot({
      authProbe: { state: "unknown" },
      manualStatus: { incidents: [] },
      localFailure: null,
    });
  }

  const cached = getCachedServiceStatusSnapshot();
  if (!force && cached?.checked_at) {
    const age = Date.now() - new Date(cached.checked_at).getTime();
    if (Number.isFinite(age) && age < SNAPSHOT_TTL_MS) return cached;
  }

  if (snapshotInFlight) return snapshotInFlight;
  snapshotInFlight = (async () => {
  const [authResult, manualResult] = await Promise.allSettled([
    probeSupabaseAuthHealth(),
    fetchManualStatus(),
  ]);

  const authProbe =
    authResult.status === "fulfilled"
      ? authResult.value
      : { state: "degraded", checked_at: new Date().toISOString() };

  const manualStatus =
    manualResult.status === "fulfilled"
      ? manualResult.value
      : getCachedManualStatus() || { incidents: [] };

  // Successful independent Auth and PostgREST checks are stronger evidence
  // than a failure reported by this device during the earlier outage.
  // Only clear the accounts marker; other service failures and manual public
  // incidents retain their existing reporting rules.
  if (
    authProbe.state === "operational" &&
    manualResult.status === "fulfilled" &&
    manualStatus?.ok === true
  ) {
    const previous = readStorage(LOCAL_FAILURE_KEY);
    if (["accounts", "login", "auth"].includes(previous?.context)) {
      removeStorage(LOCAL_FAILURE_KEY);
    }
  }

  const snapshot = buildSnapshot({
    authProbe,
    manualStatus,
    localFailure: getActiveLocalFailure(),
    manualCheckFailed: manualResult.status !== "fulfilled",
  });

  writeStorage(SNAPSHOT_CACHE_KEY, snapshot);
  return snapshot;
  })();
  try {
    return await snapshotInFlight;
  } finally {
    snapshotInFlight = null;
  }
};

export const listenForServiceStatusChanges = (callback) => {
  if (typeof window === "undefined") return () => {};

  const handler = () => callback?.();
  window.addEventListener(STATUS_EVENT, handler);
  window.addEventListener("online", handler);
  window.addEventListener("offline", handler);

  return () => {
    window.removeEventListener(STATUS_EVENT, handler);
    window.removeEventListener("online", handler);
    window.removeEventListener("offline", handler);
  };
};

export const listAdminServiceIncidents = async (limit = 50) => {
  const { data, error } = await supabase.rpc(
    "admin_list_service_status_incidents_v1",
    { p_limit: limit },
  );

  if (error) throw error;
  return Array.isArray(data) ? data : [];
};

export const saveAdminServiceIncident = async (incident) => {
  const { data, error } = await supabase.rpc(
    "admin_upsert_service_status_incident_v1",
    {
      p_incident_id: incident?.incident_id || null,
      p_provider_code: incident?.provider_code || "other",
      p_service_code: incident?.service_code || "platform",
      p_severity: incident?.severity || "degraded",
      p_status: incident?.status || "investigating",
      p_title: incident?.title || "Service disruption",
      p_public_message:
        incident?.public_message ||
        "Some UniAir services are temporarily unavailable. Please try again shortly.",
      p_internal_note: incident?.internal_note || null,
      p_affected_features: Array.isArray(incident?.affected_features)
        ? incident.affected_features
        : [],
      p_is_public: incident?.is_public !== false,
    },
  );

  if (error) throw error;
  emitStatusChange();
  return data;
};

export const resolveAdminServiceIncident = async (incidentId) => {
  const { data, error } = await supabase.rpc(
    "admin_resolve_service_status_incident_v1",
    { p_incident_id: incidentId },
  );

  if (error) throw error;
  emitStatusChange();
  return data;
};
